import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { isEqual } from 'lodash';
import {
  Button as AntButton,
  Card,
  DatePicker,
  Form,
  Input,
  notification,
  Popconfirm,
} from 'antd';
import { PlusOutlined ,CloseOutlined} from '@ant-design/icons';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { indexBy, setValueByNamePath, toDatePickerDate } from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import ParListSelect from 'components/ParListSelect';
import SaveButton from 'components/SaveButtonTabsTop';
import DeleteButton from 'components/DeleteButton';
import {
  addThirdPartyReltion,
  deleteThirdPartyReltion,
  getThirdPartyRelationListByTpid,
  Tprelationship,
  updateThirdPartyReltion,
} from 'services/thirdParty';
import FormInput from 'components/FormInput';
import ButtonOperation from 'components/ButtonOperation';
import ThirdPartyInput from 'components/ThirdPartyInput';
import moment from 'moment';
import SaveBtn from 'components/SaveBtn';
import GetLabel from 'DisplayFramework/components/GetLabel';
const NEW_RELATION_ID = -1;
export type RelationShipListCardProps = {
  id: number;
  showAsModal?: boolean;
  refetchTab?: boolean;
};
type Values = {
  ThirdPartyRelation: Tprelationship[];
};

const RelationShipListCard = (props: RelationShipListCardProps) => {
  const { id, showAsModal, refetchTab } = props;
  const [form] = Form.useForm();
  const fieldName = 'ThirdPartyRelation';
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'tprelid',
    addAsFirst: true,
  });
  const [isdisable, setDisable] = useState(false);
  const { data, refetch } = useQuery(
    ['getThirdPartyRelationListByTpid', { tpid: Number(id) }],
    () =>
      getThirdPartyRelationListByTpid({ tpid: Number(id) }).then(
        (res) => res.list || []
      ),
    { enabled: !!id }
  );

  useEffect(() => {
    refetch();
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((rl: Tprelationship) => ({
          ...rl,
          startdate: toDatePickerDate(rl.startdate),
          enddate: toDatePickerDate(rl.enddate),
          relvaluenew:
            rl.reltype === 'TP'
              ? {
                  tpid: rl.tpidtarget,
                  tpreference: rl.relvalue,
                  tpname: rl.tpTargetname,
                }
              : rl.relvalue,
        })),
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data, refetchTab, refetch]);

  const onSaveOrEdit = useCallback(
    
    async (record: any) => {
       setDisable(true)
      const relation: Tprelationship[] = form.getFieldValue(fieldName);
      const index = indexBy(relation, 'tprelid')[record.tprelid];
      const currentValue = {
        ...relation[index],
      };
      const tprecord: {
        tpid: number;
        tpreference: string;
        tpname: string;
      } = record.relvaluenew;

      const { tprelid } = currentValue;
      currentValue.reltype === ''
        ? notification.error({ message: 'Relation type is  mandatory!' })
        : (currentValue.reltype === 'TP' && tprecord?.tpreference === undefined) ||
          (currentValue.reltype === 'FREE' && record.relvaluenew === '') ||
          (currentValue.reltype === 'TAG' && record.relvaluenew === '')
        ? notification.error({ message: 'Thirdparty is  mandatory!' })
        : tprelid === NEW_RELATION_ID
        ? !currentValue.startdate
          ? notification.error({ message: 'startdate is mondatory!' })
          : await addThirdPartyReltion({
              request: {
                ...currentValue,
                tpid: id,
                relvalue:
                  currentValue.reltype === 'TP'
                    ? tprecord.tpreference
                    : record.relvaluenew,
                tpidtarget: currentValue.reltype === 'TP' ? tprecord.tpid : 0,
              },
            }).then((response) => {
              if (response.code === '200') {
                notification.success({
                  message: response.message,
                });
                setDisable(false)
                refetch();
              }
            })
        : updateThirdPartyReltion({
            tprelid: currentValue.tprelid,
            request: {
              ...currentValue,
              tpid: id,
              relvalue:
                currentValue.reltype === 'TP'
                  ? tprecord.tpreference
                  : record.relvaluenew,
              tpidtarget: currentValue.reltype === 'TP' ? tprecord.tpid : 0,
            },
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [form, refetch, id]
  );
  const getName = useCallback(
    (name: string) => (record: Tprelationship) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'tprelid')[record.tprelid];

      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<Tprelationship>[]>(
    () => [
      {
        dataIndex: 'reltype',
        render: (_value, record, index) => (
          <Form.Item>
            <FormInput noStyle name={getName('reltype')(record)}>
              <ParListSelect
                parcode="RELTYPE"
                onChange={(e)=>{setDisable(false)}}
                onSelect={(e) => {
                  if (
                    (e === 'TAG' || e === 'FREE') &&
                    record.reltype === 'TP'
                  ) {
                    setValueByNamePath(
                      form,
                      [fieldName, index, 'relvaluenew'],
                      ''
                    );
                  } else {
                    if (e === 'TP' && record.reltype === 'TP') {
                      setValueByNamePath(
                        form,
                        [fieldName, index, 'relvaluenew'],
                        {
                          tpid: record.tpidtarget,
                          tpreference: record.relvalue,
                          tpname: record.tpTargetname,
                        }
                      );
                    } else if (
                      (e === 'TAG' || e === 'FREE') &&
                      !(record.reltype === 'TP')
                    ) {
                      setValueByNamePath(
                        form,
                        [fieldName, index, 'relvaluenew'],
                        form.getFieldValue('relvalue')
                      );
                    }
                  }
                }}
              />
            </FormInput>
          </Form.Item>
        ),
      },

      {
        dataIndex: 'relcode',
        render: (_value, record, index) => (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => (
              <FormInput
                name={getName('relcode')(record)}
                rules={[{ required: true }]}
                noStyle
              >
                <ParListSelect
                  parcode="RELCODE"
                  parvalueparent={getFieldValue(getName('reltype')(record))}
                  onChange={(e) => {
                    setDisable(false);
                  }}
                />
              </FormInput>
            )}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'relvaluenew',
        render: (_value, record, index) => (
          <Form.Item shouldUpdate rules={[{ required: true }]}>
            {({ getFieldValue }) => {
              const reltype = getFieldValue(getName('reltype')(record));
              var reltypeValue;
              if (reltype === 'TP') {
                reltypeValue = (
                  <ThirdPartyInput
                    tpid={id}
                    onChange={(e) => {
                      setDisable(false);
                    }}
                  />
                );
              }
              if (reltype === 'TAG') {
                reltypeValue = (
                  <ParListSelect
                    parcode={getFieldValue(getName('relcode')(record))}
                    onChange={(e) => {
                      setDisable(false);
                    }}
                  />
                );
              }
              if (reltype === 'FREE') {
                reltypeValue = (
                  <Input
                    onChange={(e) => {
                      setDisable(false);
                    }}
                  />
                );
              }

              return (
                <FormInput
                  name={getName('relvaluenew')(record)}
                  rules={[{ required: reltypeValue === undefined }]}
                  noStyle
                >
                  {reltypeValue}
                </FormInput>
              );
            }}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'startdate',
        render: (_value, record, index) => (
          <Form.Item name={getName('startdate')(record)}>
            <DatePicker
              defaultValue={moment()}
              onChange={(e) => {
                setDisable(false);
              }}
            />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'enddate',
        render: (_value, record, index) => (
          <Form.Item name={getName('enddate')(record)}>
            <DatePicker />
          </Form.Item>
        ),
      },
      {
        key: 'actions',
        render: (_, _record) =>
          _record.tprelid === NEW_RELATION_ID ? (
            <>
              <SaveButton
                disabled={isdisable}
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                  setDisable(true);
                }}
                comcode="save"
                Inspectstyle={{ height: '32px', marginRight: 10 }}
              />
              <AntButton
                danger
                htmlType="button"
                icon={
                  <CloseOutlined height="14px" width="14px" color="white" />
                }
                onClick={() => remove(_record.tprelid)}
              />
            </>
          ) : (
            <>
              <SaveBtn
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                }}
                size="large"
                onlyIcon
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteThirdPartyReltion({
                    tprelid: _record.tprelid,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton
                  onlyIcon
                  style={{ height: '40px', width: '40px' }}
                />
              </Popconfirm>
            </>
          ),
        width: 200,
        fixed: 'right',
      },
    ],

    [form, getName, onSaveOrEdit, refetch, remove, isdisable]
  );

  const addRow = () => {
    add(row);
  };

  return (
    <Card
      style={{
        backgroundColor: '#f7f7f7',
        boxShadow:
          'rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px',
       
      }}
      bordered
      title={<GetLabel label="Tprelationship" style={{fontWeight:'bold',paddingLeft:15}}/>}
    >
      <Form form={form} initialValues={row}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const Relation: Tprelationship[] = getFieldValue(fieldName);
            const isNewItemInTable =
              Relation?.find(({ tprelid }) => tprelid === NEW_RELATION_ID) !==
              undefined;
            return (
              <DataGrid
                rowKey="id"
                dataSource={Relation}
                columns={
                  !showAsModal
                    ? columns
                    : columns.filter((cl) => cl.key !== 'actions')
                }
                hideFilters
                extraActions={
                  !showAsModal && (
                    <ButtonOperation
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={addRow}
                      disabled={isNewItemInTable}
                      comcode="add"
                    />
                  )
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </Card>
  );
};

const row: Tprelationship = {
  tprelid: NEW_RELATION_ID,
  enddate: '',
  relcode: '',
  reltype: '',
  relvalue: '',
  startdate: moment() as any,
  creationdate: '',
  tpid: 0,
  tpidtarget: 0,
  updatedate: '',
  useridcreate: 0,
  useridupdate: 0,
  tpTargetname: '',
  tpname: '',
};

export default RelationShipListCard;
